import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { Calendar, CheckCircle, ChevronRight, Loader2 } from "lucide-react";
import { PageLayout } from "../components/PageLayout";
import { Button } from "@patient-watch/ui";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useProfileEntries } from "../hooks/useProfileEntries";
import { useCopy } from "../i18n/locale";

const searchSchema = z.object({
    profile_id: z.string().uuid().optional(),
});

export const Route = createFileRoute("/form-complete")({
    component: CompleteComponent,
    validateSearch: (search) => searchSchema.parse(search),
});

function CompleteComponent() {
    const { profile_id } = useSearch({ from: "/form-complete" });
    const queryClient = useQueryClient();
    const [showContent, setShowContent] = useState(false);
    const [showEntries, setShowEntries] = useState(false);
    const [showAll, setShowAll] = useState(false);

    const { data: entries, isFetching: entriesFetching } = useProfileEntries(profile_id);
    const copy = useCopy();

    // Find incomplete entries (entries without submitted_at)
    const incompleteEntries = entries?.filter((entry) => !entry.submitted_at) ?? [];
    const hasIncompleteEntries = incompleteEntries.length > 0;

    // Add 1000ms timeout before showing content
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowContent(true);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    // Show loading state during timeout or while fetching entries
    if (!showContent || entriesFetching) {
        return (
            <PageLayout
                title={copy.complete.loadingTitle}
                description={copy.complete.loadingDescription}
                icon={Loader2}
                theme="blue"
                isLoading={true}
            >
                <div className="text-center animate-pulse">
                    <p className="text-slate-600 text-lg">{copy.complete.confirming}</p>
                </div>
            </PageLayout>
        );
    }

    // Show success with gentle reminder about incomplete entries
    if (hasIncompleteEntries) {
        return (
            <PageLayout
                title={copy.complete.entryTitle}
                description={copy.complete.entryDescription}
                icon={CheckCircle}
                theme="green"
            >
                <div className="flex flex-col justify-center items-center gap-4">
                    <p className="text-gray-600 text-md text-center">
                        {copy.complete.nextOn(dayjs().add(1, "day").format(copy.longDate))}
                    </p>
                </div>

                {/* Gentle reminder about missing entries */}
                <div className="bg-amber-50 mt-4 p-4 border border-amber-200 rounded-lg w-full">
                    <p className="text-amber-800 text-sm text-center">
                        {copy.complete.incomplete(incompleteEntries.length)}
                    </p>

                    {!showEntries ? (
                        <button
                            type="button"
                            onClick={() => setShowEntries(true)}
                            className="mt-2 w-full font-medium text-amber-700 hover:text-amber-900 text-xs underline underline-offset-2"
                        >
                            {copy.complete.showEntries}
                        </button>
                    ) : (
                        <ul className="space-y-1 mt-3">
                            {(showAll ? incompleteEntries : incompleteEntries.slice(0, 5)).map((entry) => (
                                <li key={entry.entry_id}>
                                    <Link
                                        to="/form"
                                        search={{ entry_id: entry.entry_id, profile_id: entry.profile_id }}
                                        className="flex justify-between items-center bg-amber-100/60 hover:bg-amber-200/60 px-2 py-1.5 border border-amber-200 rounded text-amber-800 text-xs transition-colors"
                                    >
                                        <span>{dayjs(entry.created_at).format("ddd, D MMM YYYY")}</span>
                                        <ChevronRight className="w-5 h-5 text-amber-600" />
                                    </Link>
                                </li>
                            ))}
                            {!showAll && incompleteEntries.length > 5 && (
                                <li>
                                    <button
                                        type="button"
                                        onClick={() => setShowAll(true)}
                                        className="pt-1 w-full font-medium text-amber-700 hover:text-amber-900 text-xs underline underline-offset-2"
                                    >
                                        {copy.complete.showAll(incompleteEntries.length)}
                                    </button>
                                </li>
                            )}
                        </ul>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-3 mt-4 w-full">
                    {profile_id && (
                        <Button
                            asChild
                            className="w-full"
                            size="lg"
                            onClick={() => {
                                queryClient.invalidateQueries({
                                    queryKey: ["profile-entries", profile_id],
                                });
                            }}
                        >
                            <Link to="/entries" search={{ profile_id }}>
                                <Calendar className="mr-2 w-5 h-5" />
                                {copy.complete.completeMissing}
                            </Link>
                        </Button>
                    )}
                    <Button asChild className="w-full" size="lg" variant="outline">
                        <Link to="/">{copy.complete.exit}</Link>
                    </Button>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout
            title={copy.complete.formTitle}
            description={copy.complete.formDescription}
            icon={CheckCircle}
            theme="green"
        >
            <div className="flex flex-col justify-center items-center gap-4">
                <p className="mb-12 text-gray-600 text-md text-center">
                    {copy.complete.nextOn(dayjs().add(1, "day").format(copy.longDate))}
                </p>
            </div>
            <Button asChild className="w-full" size="lg">
                <Link to="/">{copy.complete.exitForm}</Link>
            </Button>
            {profile_id && (
                <Button
                    asChild
                    className="w-full"
                    size="lg"
                    variant="outline"
                    onClick={() => {
                        queryClient.invalidateQueries({
                            queryKey: ["profile-entries", profile_id],
                        });
                    }}
                >
                    <Link to="/entries" search={{ profile_id }}>
                        {copy.complete.seeEntries}
                    </Link>
                </Button>
            )}
        </PageLayout>
    );
}
